import {useCallback, useEffect, useRef, useState} from "react";
import {
    getCurrentUserAccessProfile,
    type CurrentUserAccessProfile,
} from "shared/api/client";

// Automatic access-profile refresh is intentionally paused for the current
// workspace rollout. Keep the interval value beside the disabled block below
// so it can be restored later without recreating the mechanism.
// const ACCESS_PROFILE_REFRESH_MS = 30_000;

type UserAccountsAccessProfileState = {
    profile: CurrentUserAccessProfile | null;
    isLoading: boolean;
    error: Error | null;
    refresh: () => Promise<CurrentUserAccessProfile | null>;
};

function toError(value: unknown): Error {
    return value instanceof Error
        ? value
        : new Error("Failed to load the current user access profile");
}

function normalizeEmail(value: unknown): string {
    return String(value ?? "").trim().toLowerCase();
}

function positiveId(value: unknown): number | null {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Loads authorization state from the server for a User_Accounts session.
 *
 * Token claims are intentionally not used as an authorization fallback. A
 * failed refresh clears the last profile so revoked or deactivated users do
 * not keep stale UI permissions.
 */
export function useUserAccountsAccessProfile(
    enabled: boolean,
    expectedAuthUserAccountId?: string | number | null,
    expectedEmail?: string | null
): UserAccountsAccessProfileState {
    const [profile, setProfile] = useState<CurrentUserAccessProfile | null>(null);
    const [isLoading, setIsLoading] = useState(enabled);
    const [error, setError] = useState<Error | null>(null);
    const profileRef = useRef<CurrentUserAccessProfile | null>(null);
    const inFlightRef = useRef<{
        subjectKey: string;
        promise: Promise<CurrentUserAccessProfile | null>;
    } | null>(null);
    const mountedRef = useRef(false);
    const enabledRef = useRef(enabled);
    const expectedId = positiveId(expectedAuthUserAccountId);
    const normalizedExpectedEmail = normalizeEmail(expectedEmail);
    const subjectKey = `${expectedId ?? ""}:${normalizedExpectedEmail}`;
    const subjectKeyRef = useRef(subjectKey);
    enabledRef.current = enabled;
    subjectKeyRef.current = subjectKey;

    const refresh = useCallback(async (): Promise<CurrentUserAccessProfile | null> => {
        if (!enabled) return null;
        if (inFlightRef.current?.subjectKey === subjectKey) {
            return inFlightRef.current.promise;
        }

        if (!expectedId || !normalizedExpectedEmail) {
            if (mountedRef.current) {
                profileRef.current = null;
                setProfile(null);
                setError(new Error("The User_Accounts token is missing its subject identity"));
                setIsLoading(false);
            }
            return null;
        }

        if (!profileRef.current && mountedRef.current) {
            setIsLoading(true);
        }

        let request: Promise<CurrentUserAccessProfile | null>;
        request = getCurrentUserAccessProfile()
            .then((nextProfile) => {
                const profileEmail = normalizeEmail(nextProfile.user.email);
                if (
                    positiveId(nextProfile.user.authUserAccountId) !== expectedId ||
                    profileEmail !== normalizedExpectedEmail ||
                    nextProfile.user.isActive !== true
                ) {
                    throw new Error("The access profile does not match the authenticated token subject");
                }

                if (
                    mountedRef.current &&
                    enabledRef.current &&
                    subjectKeyRef.current === subjectKey
                ) {
                    profileRef.current = nextProfile;
                    setProfile(nextProfile);
                    setError(null);
                }
                return nextProfile;
            })
            .catch((value: unknown) => {
                if (
                    mountedRef.current &&
                    enabledRef.current &&
                    subjectKeyRef.current === subjectKey
                ) {
                    profileRef.current = null;
                    setProfile(null);
                    setError(toError(value));
                }
                return null;
            })
            .finally(() => {
                if (inFlightRef.current?.promise === request) {
                    inFlightRef.current = null;
                }
                if (
                    mountedRef.current &&
                    enabledRef.current &&
                    subjectKeyRef.current === subjectKey
                ) {
                    setIsLoading(false);
                }
            });

        inFlightRef.current = {subjectKey, promise: request};
        return request;
    }, [enabled, expectedId, normalizedExpectedEmail, subjectKey]);

    useEffect(() => {
        mountedRef.current = true;

        if (!enabled) {
            profileRef.current = null;
            setProfile(null);
            setError(null);
            setIsLoading(false);
            return () => {
                mountedRef.current = false;
            };
        }

        profileRef.current = null;
        setProfile(null);
        setError(null);
        setIsLoading(true);
        void refresh();

        /*
         * Temporarily disabled: the workspace must not refresh the live access
         * profile automatically on an interval, window focus, or tab visibility.
         * The initial load above and the explicit retry action remain active.
         *
         * const intervalId = window.setInterval(
         *     () => void refresh(),
         *     ACCESS_PROFILE_REFRESH_MS
         * );
         * const refreshOnFocus = () => void refresh();
         * const refreshWhenVisible = () => {
         *     if (document.visibilityState === "visible") {
         *         void refresh();
         *     }
         * };
         *
         * window.addEventListener("focus", refreshOnFocus);
         * document.addEventListener("visibilitychange", refreshWhenVisible);
         */

        return () => {
            mountedRef.current = false;
            /*
             * Cleanup for the temporarily disabled automatic refresh:
             * window.clearInterval(intervalId);
             * window.removeEventListener("focus", refreshOnFocus);
             * document.removeEventListener("visibilitychange", refreshWhenVisible);
             */
        };
    }, [enabled, refresh]);

    return {profile, isLoading, error, refresh};
}
